import PageLayout from "components/layout/PageLayout";
import { useTranslation } from "react-i18next";
import { useDeleteHealthProfileMutation, useGetHealthProfilesQuery, useSaveHealthProfileMutation } from "./api/healtProfilesApiSlice";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useState } from "react";
import { Dialog } from "primereact/dialog";
import AvatarMini from "components/commons/AvatarMini";
import HealthProfileForm from "./components/HealthProfileForm";
import HasPermission, { CanManageHealthPolicy } from "app/PermissionChecker";
import { useLocation } from "react-router-dom";

const HealthProfiles = () => {
    const [t] = useTranslation(["global", "health"]);
    let location = useLocation()
    let highlightedProfileGuid = location.state?.guid ||""

    const canManageHealthPolicy = HasPermission(CanManageHealthPolicy)
    const { data: profiles, isFetching, isSuccess, refetch } = useGetHealthProfilesQuery()



    const nameTemplate = (profile) => (
        profile.profile.isDefault ? <span className="text-muted">Default</span> : profile.profile.name
    );

    const patientsTemplate = (profile) => (
        profile.profile.isDefault ? "-" : <PatientsTemplate data={profile} />
    );

    const hrSafeZoneTemplate = (profile) => {
        const hrSafeZone = profile.profile.features.find(f => f.name === "HR-SAFEZONE");
        const hrEnabled = hrSafeZone?.enabled || false;

        return (
            <> {
                hrEnabled ?
                    <div className="d-flex bg-dark p-2 rounded-pill text-center border border-success border-3 justify-content-evenly align-items-center">
                        <span>{hrSafeZone.settings?.min}</span>
                        <small className="text-muted">-</small>
                        <span>{hrSafeZone.settings?.max} </span>
                        <small className="text-muted">bpm</small>
                    </div> : <div className="d-flex bg-dark p-2 rounded-pill  text-muted align-items-center justify-content-center">-</div>

            }</>

        );
    };

    const fallDetectionTemplate = (profile) => {
        const fallDetection = profile.profile.features.find(f => f.name === "FALL-DETECTION");
        return (<>
            {
                fallDetection.enabled ?
                    <div className="d-flex bg-dark p-2 rounded-pill text-center border border-success border-3 justify-content-center align-items-center gap-2 ">
                        <span>{fallDetection.settings?.threshold}</span>
                        <small className="text-muted">m/s<sup>2</sup></small>
                    </div> : <div className="d-flex bg-dark p-2 rounded-pill  text-muted align-items-center justify-content-center">-</div>
            }
        </>);
    };

    const inactivtyTemplate = (profile) => {
        const inactivity = profile.profile.features.find(f => f.name === "INACTIVITY");
        return (<>
            {
                inactivity.enabled ?
                    <div className="d-flex bg-dark p-2 rounded-pill text-center border border-success border-3 justify-content-center align-items-center gap-2 ">
                        <span>{inactivity.settings?.threshold}</span>
                        <small className="text-muted">m/s<sup>2</sup></small>
                    </div> : <div className="d-flex bg-dark p-2 rounded-pill  text-muted align-items-center justify-content-center">-</div>
            }
        </>);
    };

    const trackingTemplate = (profile) => {
        const hrTracking = profile.profile.features.find(f => f.name === "HR");
        const bpTracking = profile.profile.features.find(f => f.name === "BP");
        const stepsTracking = profile.profile.features.find(f => f.name === "STEPS");
        const tempTracking = profile.profile.features.find(f => f.name === "TEMPS");
        const vo2MaxTracking = profile.profile.features.find(f => f.name === "VO2MAX");
        return (
            <div>
                <div className="d-flex text-h5">
                    <div className={`badge rounded-pill ${hrTracking?.enabled != true && "opacity-25"}`}>
                        <i className="fa-solid fa-heart-pulse"></i>
                    </div>
                    <div className={`badge rounded-pill  ${bpTracking?.enabled != true && "opacity-25"}`}>
                        <i className="fa-solid fa-wind"></i>
                    </div>
                    <div className={`badge rounded-pill  ${stepsTracking?.enabled != true && "opacity-25"}`}>
                        <i className="fa-solid fa-shoe-prints"></i>
                    </div>
                    <div className={`badge rounded-pill  ${tempTracking?.enabled != true && "opacity-25"}`}>
                        <i className="fa-solid fa-temperature-three-quarters"></i>
                    </div>
                    <div className={`badge rounded-pill  ${vo2MaxTracking?.enabled != true && "opacity-25"}`}>
                        VO<sub>2</sub>MAX
                    </div>
                </div>
            </div>
        );
    };

    const safeGuardHeader = (featureName, icon) => {
        return (
            <div>
                <span className="text-eh small">SafeGuard</span><br />
                {icon} {featureName}
            </div>
        )
    }

    const dataTrackingHeader = () => {
        return (
            <div>
                <span className="text-secondary small">Data</span><br />
                <i className="fa-solid fa-chart-line opacity-50"></i> Tracking
            </div>
        )
    }

    const emergencyNumberHeader = () => {
        return (
            <div>
                <span className="text-danger small">Emergency</span><br />
                <i className="fa-solid fa-phone opacity-50"></i>  Phone Number
            </div>
        )
    }


    const emergencyPhoneTemplate = (profile) => profile.profile.emergencyNumber;

    const actionTemplate = (profile) => {
        return (
            <div>
                <EditHealthProfileDialog data={profile} />
                <DeleteProfileTemplate data={profile} />
            </div>
        )
    };

    let content;
    if (isSuccess) {
        content = (
            <div>
                {
                    canManageHealthPolicy &&
                    <div className=" text-end"><NewHealthProfileDialog /></div>
                }
                <DataTable
                    dataKey="profile.guid"
                    value={profiles}
                    paginator
                    rows={25}
                    stripedRows
                    tableStyle={{ minWidth: '50rem' }}
                    rowClassName={(row) => ({ 'bg-success bg-opacity-25': row.profile.guid == highlightedProfileGuid })}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                >
                    <Column field="profile.name" header={t("property.name")} body={nameTemplate} />
                    <Column field="profile.features" header={safeGuardHeader("HR SafeZone", <i className="fa-solid fa-heart-pulse opacity-50"></i>)} body={hrSafeZoneTemplate} />
                    <Column field="profile.features" header={safeGuardHeader(t("health:entity.profile.features.FALL-DETECTION_short"), <i className="fa-solid fa-person-falling opacity-50"></i>)} body={fallDetectionTemplate} />
                    <Column field="profile.features" header={safeGuardHeader(t("health:entity.profile.features.INACTIVITY_short"), <i className="fa-solid fa-notes-medical opacity-50"></i>)} body={inactivtyTemplate} />
                    <Column field="profile.features" header={dataTrackingHeader} body={trackingTemplate} />
                    <Column field="profile.features" header={emergencyNumberHeader} body={emergencyPhoneTemplate} />
                    <Column field="profile.patients" header={<i className="fa-solid fa-users"></i>} body={patientsTemplate} />
                    {
                        canManageHealthPolicy &&
                        <Column field="profile.patients" header={t("property.actions")} body={actionTemplate} />
                    }

                </DataTable>

            </div>

        );
    }

    return (
        <PageLayout
            pageTitle={t("health:menu.profiles_ext")}
            isLoadingContent={isFetching}
            onPageRefresh={refetch}
            bodyContent={content}
        />
    );
};

export default HealthProfiles;



const NewHealthProfileDialog = () => {
    const [t] = useTranslation(["global", "health"]);
    const [openDialog, setOpenDialog] = useState(false);
    const [createProfile, { isLoading }] = useSaveHealthProfileMutation();

    const handleNewProfile = async (payload) => {
        await createProfile(payload);
        setOpenDialog(false);
    };

    return (
        <>
            <button className="btn btn-primary" onClick={() => setOpenDialog(true)}> + {t("action.add")}</button>
            <Dialog header={t("health:actions.new_profile")} visible={openDialog} onHide={() => setOpenDialog(false)} >
                <HealthProfileForm
                    onSave={handleNewProfile}
                    onCancel={() => setOpenDialog(false)}
                    isEditMode={false}
                />
            </Dialog>
        </>
    );
};

const EditHealthProfileDialog = ({ data }) => {
    const [t] = useTranslation(["global", "health"]);
    const [openDialog, setOpenDialog] = useState(false);
    const [saveProfile, { isLoading }] = useSaveHealthProfileMutation();

    const handleEditProfile = async (payload) => {
        await saveProfile(payload);
        setOpenDialog(false);
    };
    return (
        <>
            <button className="btn btn-light" onClick={() => setOpenDialog(true)}><i className="fa-solid fa-pen"></i></button>
            <Dialog header={t("health:actions.edit_profile")} visible={openDialog} onHide={() => setOpenDialog(false)} >
                <HealthProfileForm
                    initialData={data.profile}
                    onSave={handleEditProfile}
                    onCancel={() => setOpenDialog(false)}
                    isEditMode={true}
                />
            </Dialog>
        </>
    );
};

const DeleteProfileTemplate = ({ data }) => {
    const [t] = useTranslation(["global", "health"]);
    const [openDialog, setOpenDialog] = useState(false);
    const [deleteProfile] = useDeleteHealthProfileMutation()
    return (
        <>
            <button className="btn btn-danger m-1" onClick={() => setOpenDialog(true)}><i className="fa-solid fa-trash"></i></button>
            <Dialog header={t("health:actions.delete_profile")} visible={openDialog} onHide={() => setOpenDialog(false)}>
                <span>{t("health:helper.profile_delete_confirm")} <b>{data.profile.name}</b> ?</span>
                <div className="text-end mt-4">
                    <button className="btn btn-danger mx-2" onClick={() => {
                        deleteProfile(data.profile.guid)
                        setOpenDialog(false)
                    }}>  {t("action.delete")} </button>
                    <button className="btn btn-secondary" onClick={() => setOpenDialog(false)}>{t("action.cancel")}</button>
                </div>

            </Dialog>
        </>
    );
};

const PatientsTemplate = ({ data }) => {
    const [t] = useTranslation(["global", "health"]);
    const [openDialog, setOpenDialog] = useState(false);
    return (
        <>
            <button className="btn btn-secondary m-1" onClick={() => setOpenDialog(true)}>
                <i className="fa-solid fa-user"></i> {data.patients.length}
            </button>
            <Dialog visible={openDialog} onHide={() => setOpenDialog(false)}>
                <h4>{data.profile.name}</h4>
                <hr />
                <div className="p-2">{t("health:helper.assigned_to")}</div>
                {data.patients.map(p => (
                    <AvatarMini displayName={p.displayName} guid={p.guid} key={p.guid} destination="/dashboard/health/history" />
                ))}
            </Dialog>
        </>
    );
};
